export const PRIVACY_CONSENT_KEY = 'bilt_privacy_consent_v1';
export const PRIVACY_CONSENT_VERSION = '2026-08-14';

export type ConsentCategories = {
  necessary: true;
  analytics: boolean;
  advertising: boolean;
  personalization: boolean;
};

export type PrivacyConsent = {
  version: string;
  timestamp: string;
  categories: ConsentCategories;
  gpc: boolean;
};

export function detectGpc(): boolean {
  try {
    return Boolean((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl);
  } catch {
    return false;
  }
}

export function getPrivacyConsent(): PrivacyConsent | null {
  try {
    const raw = localStorage.getItem(PRIVACY_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PrivacyConsent;
    if (!parsed?.version || !parsed?.categories) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function hasPrivacyConsentChoice(): boolean {
  return getPrivacyConsent() !== null;
}

export function savePrivacyConsent(
  partial: Partial<ConsentCategories> & { source?: string } = {}
): PrivacyConsent {
  const gpc = detectGpc();
  const categories: ConsentCategories = {
    necessary: true,
    analytics: Boolean(partial.analytics),
    advertising: gpc ? false : Boolean(partial.advertising),
    personalization: Boolean(partial.personalization),
  };
  const record: PrivacyConsent = {
    version: PRIVACY_CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    categories,
    gpc,
  };
  localStorage.setItem(PRIVACY_CONSENT_KEY, JSON.stringify(record));
  window.dispatchEvent(new CustomEvent('bilt:privacy-consent', { detail: record }));
  return record;
}

export function acceptAllPrivacyConsent(): PrivacyConsent {
  return savePrivacyConsent({ analytics: true, advertising: !detectGpc(), personalization: true });
}

export function rejectOptionalPrivacyConsent(): PrivacyConsent {
  return savePrivacyConsent({ analytics: false, advertising: false, personalization: false });
}

export function canUseAnalytics(): boolean {
  const c = getPrivacyConsent();
  return Boolean(c?.categories.analytics);
}

export function canUseAdvertising(): boolean {
  if (detectGpc()) return false;
  const c = getPrivacyConsent();
  if (c?.gpc) return false;
  return Boolean(c?.categories.advertising);
}

/** Apply GPC as an advertising opt-out even before the banner is dismissed. */
export function applyGpcIfPresent(): PrivacyConsent | null {
  if (!detectGpc()) return getPrivacyConsent();
  const existing = getPrivacyConsent();
  if (existing && existing.categories.advertising === false && existing.gpc) {
    return existing;
  }
  return savePrivacyConsent({
    analytics: existing?.categories.analytics ?? false,
    advertising: false,
    personalization: existing?.categories.personalization ?? false,
  });
}
